import React, { useEffect, useState } from 'react';
import { BusinessOwner, Container, ErrorMessageBox, FormList, FormListButtonBox, FormListRow } from '../styles';
import { Field, Form, Formik, useFormikContext } from 'formik';
import UploadImage from 'components/UploadImage/field';
import SelectComponent from 'components/Select';
import { TradeMyBusinessNewPageProps, states } from '../utils';
import Button from 'components/Button';
import { accomodationsInitialValues, accomodationsValidationSchema } from './utils';
import FieldMask from 'components/FieldMask';
import ErrorMessage from 'components/ErrorMessage';
import TextArea from 'components/TextArea';
import { useHistory, useLocation } from 'react-router-dom';
import { useLoading } from 'hooks/useLoading';
import TouristOffersBusinessService from 'services/TouristOffersBusinessService';
import ImageService from 'services/ImageService';
import MessageModal from 'common/MessageModal';
import ArrayField from 'components/ArrayField';
import ConfirmationModal from 'components/ConfirmationModal';
import questionMarkIcon from 'assets/images/questionMarkIcon.svg';
import Tooltip from 'components/Tooltip';
import { placeIDLink } from 'utils/placeIDLink';
import AccommodationCategoryService from 'services/AccommodationCategoryService';

import ValidationWarningModal from 'components/ValidationWarningModal';
import { checkBusinessWarnings } from 'utils/formWarnings';
import LimitedField from 'components/LimitedField';

const MyBusinessAccomodationsForm = ({ selectedId, item, type }: TradeMyBusinessNewPageProps): JSX.Element => {
  const { openLoading, closeLoading } = useLoading();
  const history = useHistory();
  const location = useLocation<{ from?: string }>();
  const redirectUrl = location.state?.from || '/admin/trade/my-business';
  const [messageInfo, setMessageInfo] = useState({ title: '', message: '' });
  const [showModal, setShowModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [formWarnings, setFormWarnings] = useState<string[]>([]);
  const [accomodationCategories, setAccomodationCategories] = useState<{ label: string; value: string }[]>([]);

  const touristCorridors = [
    { label: "Arraial D'Ajuda", value: "Arraial D'Ajuda" },
    { label: 'Caraíva', value: 'Caraíva' },
    { label: 'Centro da Cidade', value: 'Centro da Cidade' },
    { label: 'Centro Histórico', value: 'Centro Histórico' },
    { label: 'Espelho', value: 'Espelho' },
    { label: 'Litoral Norte', value: 'Litoral Norte' },
    { label: 'Porto Seguro', value: 'Porto Seguro' },
    { label: 'Trancoso', value: 'Trancoso' },
    { label: 'Vale Verde', value: 'Vale Verde' },
  ];

  const service = new TouristOffersBusinessService();
  const imageService = new ImageService();
  const accomodationCategoryService = new AccommodationCategoryService();

  useEffect(() => {
    if (selectedId) {
      console.log('selectedId', selectedId);
    }
  }, [selectedId]);

  useEffect(() => {
    fetchAccomodationCategories();
  }, []);

  const fetchAccomodationCategories = async () => {
    const categories = await accomodationCategoryService.findAll();
    const formattedCategories = categories.map(category => ({
      label: category.description,
      value: category.description,
    }));
    setAccomodationCategories(formattedCategories);
  };

  const onSubmit = async (values: any) => {
    openLoading();
    try {
      const [images, image, structures] = await Promise.all([
        values.images?.length ? imageService.uploadMultiple(values.images) : [],
        values.image?.length ? imageService.uploadMultiple(values.image) : [],
        values.structures?.length ? imageService.uploadMultiple(values.structures) : [],
      ]);

      const restoredImages = (images || []).map((image: string) => ({
        image,
        description: '',
      }));

      const restoredStructuresImages = (structures || []).map((image: string) => ({
        image,
        description: 'Estruturas Anexas',
      }));

      const restoredObject = {
        ...values,
        images: [...restoredImages, ...restoredStructuresImages],
        image: image?.[0] || '',
      };

      selectedId && selectedId.length > 0
        ? await service.update(Number(selectedId), restoredObject)
        : await service.create({
            ...restoredObject,
            type: type,
            businessId: item?.status === 'approved' ? item?.id : null,
          });

      setMessageInfo({ title: 'Sucesso', message: 'Dados salvos com sucesso' });
      setShowModal(true);
    } catch (error: any) {
      console.error(error);
      setMessageInfo({ title: 'Erro', message: 'Erro ao salvar os dados' });
      setShowModal(true);
    }
    closeLoading();
  };

  const closeModal = () => {
    setShowModal(false);

    if (messageInfo.title === 'Sucesso') {
      history.push(redirectUrl);
    }
  };

  return (
    <Container>
      <Formik
        initialValues={{
          ...accomodationsInitialValues,
          ...item,
        }}
        validationSchema={accomodationsValidationSchema}
        onSubmit={values => {
          const warnings = checkBusinessWarnings(values);
          if (warnings.length > 0) {
            setFormWarnings(warnings);
            setShowWarningModal(true);
          } else {
            setShowConfirmModal(true);
          }
        }}
        enableReinitialize>
        {formik => (
          <Form>
            <FormList>
              <FormListRow className="col-4">
                <LimitedField
                  id="businessRepresentative"
                  name="businessRepresentative"
                  label="Nome Completo do Responsável Comercial"
                  maxLength={255}
                />
                <ErrorMessageBox className="with-limited-field">
                  <ErrorMessage name="businessRepresentative" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow className="col-4">
                <label htmlFor="businessOwner">Nome Completo do(s) Proprietário(s)</label>
                <BusinessOwner>
                  <ArrayField name="businessOwner" />
                </BusinessOwner>
              </FormListRow>
              <FormListRow>
                <SelectComponent
                  name="businessType"
                  options={accomodationCategories}
                  label="Classificação"
                  onChange={value => formik.setFieldValue('businessType', value)}
                  selected={formik.values.businessType ? [formik.values.businessType] : []}
                />
                <ErrorMessageBox>
                  <ErrorMessage name="businessType" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow>
                <SelectComponent
                  name="touristCorridor"
                  options={touristCorridors}
                  label="Corredor Turístico"
                  onChange={value => formik.setFieldValue('touristCorridor', value)}
                  selected={formik.values.touristCorridor ? [formik.values.touristCorridor] : []}
                />
                <ErrorMessageBox>
                  <ErrorMessage name="touristCorridor" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow>
                <LimitedField id="tradeName" name="tradeName" label="Nome Fantasia" maxLength={255} />
                <ErrorMessageBox className="with-limited-field">
                  <ErrorMessage name="tradeName" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow>
                <LimitedField
                  id="title"
                  name="title"
                  label={
                    <span style={{ display: 'flex', alignItems: 'center', color: '#272727' }}>
                      Título da Atividade
                      <Tooltip text="Campo referente ao título que aparecerá no card para visualização do turista." />
                    </span>
                  }
                  maxLength={255}
                />
                <ErrorMessageBox className="with-limited-field">
                  <ErrorMessage name="title" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow>
                <label htmlFor="document">CNPJ</label>
                <FieldMask type="cnpj" id="document">
                  <Field id="document" name="document" type="text" />
                </FieldMask>
                <ErrorMessageBox>
                  <ErrorMessage name="document" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow>
                <label htmlFor="address.zipCode">CEP</label>
                <FieldMask type="zipcode" id="zipCode">
                  <Field id="address.zipCode" name="address.zipCode" type="text" />
                </FieldMask>
                <ErrorMessageBox>
                  <ErrorMessage name="address.zipCode" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow className="col-2">
                <LimitedField id="address.street" name="address.street" label="Endereço" maxLength={255} />
                <ErrorMessageBox className="with-limited-field">
                  <ErrorMessage name="address.street" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow>
                <label htmlFor="address.number">Número</label>
                <Field id="address.number" name="address.number" type="text" />
                <ErrorMessageBox>
                  <ErrorMessage name="address.number" />
                </ErrorMessageBox>
              </FormListRow>

              <FormListRow>
                <LimitedField
                  id="address.additionalData"
                  name="address.additionalData"
                  label="Complemento"
                  maxLength={255}
                />
                <ErrorMessageBox className="with-limited-field">
                  <ErrorMessage name="address.additionalData" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow>
                <LimitedField id="address.neighborhood" name="address.neighborhood" label="Bairro" maxLength={255} />
                <ErrorMessageBox className="with-limited-field">
                  <ErrorMessage name="address.neighborhood" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow>
                <LimitedField id="address.city" name="address.city" label="Cidade" maxLength={255} />
                <ErrorMessageBox className="with-limited-field">
                  <ErrorMessage name="address.city" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow>
                <SelectComponent
                  name="address.state"
                  options={states}
                  label="Estado"
                  onChange={value => formik.setFieldValue('address.state', value)}
                  selected={formik.values.address.state ? [formik.values.address.state] : []}
                />

                <ErrorMessageBox>
                  <ErrorMessage name="address.state" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow>
                <label htmlFor="googleMapsLink" style={{ display: 'flex', alignItems: 'center' }}>
                  Place ID do Google Maps
                  <a
                    href={placeIDLink}
                    target="_blank"
                    rel="noreferrer"
                    title="Clique aqui para baixar o PDF de instruções sobre o PlaceID do Google Maps">
                    <img src={questionMarkIcon} alt="Mais informações" />
                  </a>
                </label>
                <Field id="googleMapsLink" name="googleMapsLink" type="text" />
                <ErrorMessageBox>
                  <ErrorMessage name="googleMapsLink" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow>
                <label htmlFor="accommodation.numberOfRooms">Nº de Aptos</label>
                <Field id="accommodation.numberOfRooms" name="accommodation.numberOfRooms" type="text" />
                <ErrorMessageBox>
                  <ErrorMessage name="accommodation.numberOfRooms" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow>
                <LimitedField
                  id="accommodation.category"
                  name="accommodation.category"
                  label={
                    <span style={{ display: 'flex', alignItems: 'center', color: '#272727' }}>
                      Categoria dos Apartamentos
                      <Tooltip text="Preencha esse campo com as categorias dos quartos da sua hospedagem. Ex.: Luxo, standard, VIP, premium..." />
                    </span>
                  }
                  maxLength={255}
                />
                <ErrorMessageBox className="with-limited-field">
                  <ErrorMessage name="accommodation.category" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow>
                <label htmlFor="accommodation.numberOfBeds">Número de Leitos</label>
                <Field id="accommodation.numberOfBeds" name="accommodation.numberOfBeds" type="text" />
                <ErrorMessageBox>
                  <ErrorMessage name="accommodation.numberOfBeds" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow className="col-4">
                <TextArea maxLength={750} name="description" title="Descrição" />
              </FormListRow>
              <FormListRow className="col-4">
                <TextArea
                  maxLength={2000}
                  name="accommodation.description"
                  title="Estruturas Anexas (barracas, beach club, parque aquático...)"
                />
              </FormListRow>
              <FormListRow className="col-4">
                <UploadImage
                  descriptionText="Imagens das Estruturas Anexas: Adicione até 5 imagens. Para garantir a qualidade da visualização, as
              imagens precisam ter dimensão mínima de 572 x 432 pixels"
                  multipleFiles
                  limitFiles={5}
                  htmlFor="structures"
                  name="structures"
                />
              </FormListRow>
              <FormListRow>
                <label htmlFor="">Telefone</label>
                <FieldMask type="phone" id="phone">
                  <Field id="phone" name="phone" type="text" />
                </FieldMask>
                <ErrorMessageBox>
                  <ErrorMessage name="phone" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow>
                <label htmlFor="whatsapp">WhatsApp</label>
                <FieldMask type="phone" id="whatsapp">
                  <Field id="whatsapp" name="whatsapp" type="text" />
                </FieldMask>
                <ErrorMessageBox>
                  <ErrorMessage name="whatsapp" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow className="col-2">
                <label htmlFor="tradeEmail">E-mail Comercial</label>
                <FieldMask type="email" id="tradeEmail">
                  <Field id="tradeEmail" name="tradeEmail" type="text" />
                </FieldMask>
                <ErrorMessageBox>
                  <ErrorMessage name="tradeEmail" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow>
                <label htmlFor="youtubeLink">Link de Vídeo do YouTube</label>
                <Field id="youtubeLink" name="youtubeLink" type="text" />
                <ErrorMessageBox>
                  <ErrorMessage name="youtubeLink" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow>
                <label htmlFor="site">Link do Site</label>
                <Field id="site" name="site" type="text" />
                <ErrorMessageBox>
                  <ErrorMessage name="site" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow>
                <label htmlFor="instagramLink">Link do Instagram</label>
                <Field id="instagramLink" name="instagramLink" type="text" />
                <ErrorMessageBox>
                  <ErrorMessage name="instagramLink" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow>
                <label htmlFor="facebookLink">Link do Facebook</label>
                <Field id="facebookLink" name="facebookLink" type="text" />
                <ErrorMessageBox>
                  <ErrorMessage name="facebookLink" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow className="col-4">
                <UploadImage
                  descriptionText="Imagem do Card: Adicione 1 imagem. Para garantir a qualidade da visualização, as imagens precisam ter dimensão mínima de 500 x 600 pixels"
                  multipleFiles={false}
                  limitFiles={1}
                  htmlFor="image"
                  name="image"
                />
                <ErrorMessageBox className="for-uploadimages">
                  <ErrorMessage name="image" />
                </ErrorMessageBox>
              </FormListRow>
              <FormListRow className="col-4">
                <UploadImage
                  descriptionText="Imagens da Biblioteca: Adicione até 12 imagens. Para garantir a qualidade da visualização, as imagens precisam ter dimensão mínima de 572 x 432 pixels"
                  multipleFiles
                  limitFiles={12}
                  htmlFor="images"
                  name="images"
                />
                <ErrorMessageBox className="for-uploadimages">
                  <ErrorMessage name="images" />
                </ErrorMessageBox>
              </FormListRow>

              <FormListRow className="col-4">
                <FormListButtonBox>
                  <Button type="button" buttonType="secondary" onClick={() => history.push(redirectUrl)}>
                    Cancelar
                  </Button>
                  <Button type="submit">{selectedId && selectedId.length > 0 ? 'Salvar' : 'Criar'}</Button>
                </FormListButtonBox>
              </FormListRow>
            </FormList>
            <ConfirmationModal
              message="O cadastro do seu negócio precisa ser aprovado pela Secretaria do Turismo. Após o envio do formulário, você só poderá editar os dados de negócios aprovados. Deseja Continuar?"
              title="Cadastro de Negócio"
              onConfirm={() => {
                onSubmit(formik.values);
                setShowConfirmModal(false);
              }}
              onCancel={() => setShowConfirmModal(false)}
              confirmText="Enviar"
              cancelText="Cancelar"
              open={showConfirmModal}
              type="submit"
            />
            <ValidationWarningModal
              open={showWarningModal}
              warnings={formWarnings}
              onConfirm={() => {
                setShowWarningModal(false);
                setShowConfirmModal(true);
              }}
              onCancel={() => setShowWarningModal(false)}
            />
          </Form>
        )}
      </Formik>

      <MessageModal isOpen={showModal} title={messageInfo.title} message={messageInfo.message} onClose={closeModal} />
    </Container>
  );
};

export default MyBusinessAccomodationsForm;
